import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { ArrowLeft, Eye, EyeOff, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function PasswordField({ id, value, onChange, placeholder, dataTestId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative mt-1.5">
      <Input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        autoComplete="new-password"
        className="border-af focus-visible:ring-terracotta pr-11"
        data-testid={dataTestId}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${dataTestId}-toggle`}
        aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
        className="absolute inset-y-0 right-0 w-10 flex items-center justify-center text-af-muted hover:text-terracotta transition-colors"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd.next });
      setDone(true);
      setTimeout(() => navigate("/auth/login"), 2500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Lien invalide ou expiré.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-af grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-af-hero border-r border-af">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={52} />
          <span className="font-display font-extrabold text-2xl">AfriMeet</span>
        </Link>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-4">
            Choisissez un nouveau mot de passe.
          </h2>
          <p className="text-af-secondary leading-relaxed max-w-md">
            Utilisez au moins 8 caractères et évitez les mots de passe que vous utilisez ailleurs.
          </p>
        </div>
        <div className="text-xs text-af-muted">© 2026 AfriMeet</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Link to="/" className="flex items-center gap-3">
              <Logo size={52} />
              <span className="font-display font-extrabold text-2xl">AfriMeet</span>
            </Link>
          </div>

          {!token ? (
            <div data-testid="reset-no-token-card">
              <div className="h-12 w-12 rounded-full bg-[#FDECEC] flex items-center justify-center mb-5">
                <AlertTriangle className="h-6 w-6 text-[#9F1239]" />
              </div>
              <h1 className="font-display font-extrabold text-3xl mb-2">Lien invalide</h1>
              <p className="text-af-secondary">
                Le lien semble incomplet ou n'est plus valide. Demandez un nouveau lien.
              </p>
              <Link
                to="/auth/forgot-password"
                data-testid="reset-request-new"
                className="inline-flex items-center gap-2 mt-8 text-terracotta font-semibold hover:underline"
              >
                Demander un nouveau lien
              </Link>
            </div>
          ) : done ? (
            <div data-testid="reset-done-card">
              <div className="h-12 w-12 rounded-full bg-[#F5EFD8] flex items-center justify-center mb-5">
                <CheckCircle2 className="h-6 w-6 text-terracotta" />
              </div>
              <h1 className="font-display font-extrabold text-3xl mb-2">Mot de passe modifié</h1>
              <p className="text-af-secondary">
                Votre mot de passe a été réinitialisé avec succès. Redirection vers la connexion…
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2 text-terracotta">
                <Lock className="h-5 w-5" />
                <span className="text-sm font-semibold uppercase tracking-widest">Réinitialisation</span>
              </div>
              <h1 className="font-display font-extrabold text-3xl mb-2">Nouveau mot de passe</h1>
              <p className="text-af-secondary mb-8">
                Choisissez un mot de passe robuste pour sécuriser votre compte.
              </p>
              <form onSubmit={onSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="pwd-next">Nouveau mot de passe</Label>
                  <PasswordField
                    id="pwd-next"
                    value={pwd.next}
                    onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                    placeholder="Au moins 8 caractères"
                    dataTestId="reset-pwd-next"
                  />
                </div>
                <div>
                  <Label htmlFor="pwd-confirm">Confirmer le mot de passe</Label>
                  <PasswordField
                    id="pwd-confirm"
                    value={pwd.confirm}
                    onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                    placeholder="Retapez le nouveau mot de passe"
                    dataTestId="reset-pwd-confirm"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading || !pwd.next || !pwd.confirm}
                  data-testid="reset-submit-btn"
                  className="w-full bg-terracotta hover:bg-[#9C7124] text-white h-11 rounded-md"
                >
                  {loading ? "Modification…" : "Réinitialiser mon mot de passe"}
                </Button>
              </form>
              <Link
                to="/auth/login"
                data-testid="reset-back-login"
                className="inline-flex items-center gap-2 mt-6 text-terracotta font-semibold hover:underline text-sm"
              >
                <ArrowLeft className="h-4 w-4" /> Retour à la connexion
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
